import { atom } from "jotai";
import { atomFamily } from "jotai-family";
import { atomWithDefault } from "jotai/utils";
import { isEqual } from "lodash-es";
import { config } from "../../config";
import { getTokenAtom } from "../../server/auth/store";
import { authHeader } from "../../utils/auth-header";
import { loadable } from "../../utils/jotai-loadable";
import { notEmpty } from "../../utils/not-empty";
import { brandKitStyles } from "../brand-kit/hooks/use-brand-kit";
import { brandingAtom } from "../brand-kit/store";
import { withContentTracking } from "../content-generation/track-content-generation";
import { getPageNumber } from "../design-huddle/utils";
import { brandToneAtom, debugLabel, loadableImagePreviewURLFamily } from "../generate/store";
import { selfDriving } from "../self-driving/self-driving";
import { libraryAtom, libraryQueryAtom, libraryTemplateFamily } from "./library/store";
import { atlasV2Search } from "./server/atlas-search";

import type { BrandTone } from "../brand-kit/server/brand-tone-service";
import type { Project } from "../design-huddle/types";
import type {
  LoadableTemplateContent,
  Template,
  TemplateContent,
  TemplateImage,
} from "../generic/atoms/types/template";
import type { LibraryQuery, LibraryTemplate } from "./library/types";
import type { DiscoverTemplateImageQuery } from "./types";

export const unstableAtlasSearchAtom = atom(false);

/* a design huddle project to override a specific image on the template */
export const discoverTemplateDHProjectFamily = atomFamily(
  ({ slug, pageNumber }: { slug?: string; pageNumber?: number }) =>
    debugLabel(`discoverTemplateDHProjectFamily${slug}/${pageNumber}`, atom<Project | null>(null)),
  isEqual,
);

/* design huddle allows the user to change the page, so if they are editing page 1 they can change the page to 2
   it would be better if either
   a) we could restrict which pages are available in the project, but the design huddle api doesn't allow this
   b) we could use a single project, but some projects have more than 5 pages and we don't have a way of representing this on the front-end
  */
export const discoverTemplateImagePageOverrideFamily = atomFamily(
  ({ slug, pageNumber }: { slug?: string; pageNumber?: number }) =>
    debugLabel(
      `discoverTemplateImagePageOverrideFamily/${slug}/${pageNumber}`,
      atom<number | null>(null),
    ),
  isEqual,
);

export const discoverTemplateImageFamily = atomFamily(
  ({ slug, img, style }: DiscoverTemplateImageQuery) =>
    debugLabel(
      `discoverTemplateImageFamily/${slug}/${JSON.stringify(img)}/${style.value}`,

      atomWithDefault<TemplateImage | null>((get) => {
        if (!img) {
          return null;
        }

        const brandInfoSelected = get(brandingAtom);
        const pageNumber = getPageNumber({ ...brandInfoSelected, branding_type: style });

        const dhProject = get(discoverTemplateDHProjectFamily({ slug, pageNumber }));
        const pageNumberOverride = get(
          discoverTemplateImagePageOverrideFamily({ slug, pageNumber }),
        );

        const info = {
          template_code: dhProject ? dhProject.project_id : img.template_code,
          page_number: pageNumberOverride ?? pageNumber,
          header: dhProject ? undefined : img.header,
          subheader: dhProject ? undefined : img.subheader,
          unsplashExtra: undefined,
          stableDiffusionImage: false,
          dhProject,
        };

        const url = get(loadableImagePreviewURLFamily(info));

        return { ...info, url, style };
      }),
    ),
  isEqual,
);

/**
 * `discoverTemplateImageFamily` is read as a loadable everywhere it is consumed,
 * so the wrapper lives here rather than at each call site — one derived atom per
 * key, created once.
 */
export const loadableDiscoverTemplateImageFamily = atomFamily(
  (query: DiscoverTemplateImageQuery) => loadable(discoverTemplateImageFamily(query)),
  isEqual,
);

export const discoverContentFamily = atomFamily(
  ({
    template,
    contentKey,
    customTone,
  }: {
    template: LibraryTemplate | null;
    contentKey: keyof LibraryTemplate["content"];
    customTone?: BrandTone;
  }) =>
    debugLabel(
      `discoverContentFamily/${template?.slug}-${contentKey}${customTone ? `-${customTone.name}` : ""}`,
      atomWithDefault((get): TemplateContent | null | Promise<TemplateContent | null> => {
        if (!template) {
          return null;
        }

        if (contentKey === "custom" && customTone) {
          return get(customToneContentFamily({ customTone, template }));
        }

        const content = template.content[contentKey];

        if (!content) {
          return null;
        }

        return {
          type: "static" as const,
          data: { response: { content } },
        };
      }),
    ),
  isEqual,
);

/** As above: `discoverContentFamily` is always read as a loadable. */
const loadableDiscoverContentFamily = atomFamily(
  (query: Parameters<typeof discoverContentFamily>[0]) => loadable(discoverContentFamily(query)),
  isEqual,
);

const customToneContentFamily = atomFamily(
  ({ customTone, template }: { customTone: BrandTone; template: LibraryTemplate }) =>
    debugLabel(
      `customToneContentFamily/${template.slug}/${customTone.name}`,
      atom(async (get): Promise<TemplateContent | null> => {
        const token = await get(getTokenAtom).getToken();

        if (!template || !token) return null;

        const { data, contentGenerationId, contentGenerationResultId } = await withContentTracking(
          token,
          {
            contentType: "moment",
            subType: "discover_customise",
            prompt: JSON.stringify({ toneSummary: customTone.summary }),
          },
          async () => {
            const { data: result, error } = await selfDriving.POST("/discover/customise", {
              body: {
                professional: template.content.professional,
                casual: template.content.casual,
                direct: template.content.direct,
                toneSummary: customTone.summary,
              },
              params: {
                query: { model: config.gpt_model },
              },
              ...authHeader(token),
            });

            if (error || !result) throw new Error(error.error || "Request failed");
            return result;
          },
        );

        return {
          type: "static" as const,
          data: { response: { content: data.response } },
          contentGenerationId,
          contentGenerationResultId,
        };
      }),
    ),
  isEqual,
);

export const discoverTemplateAtom = atom<Template | null>((get) => {
  const query = get(libraryQueryAtom);
  return get(discoverTemplateFamily(query));
});

export const loadableDiscoverTemplateAtom = loadable(discoverTemplateAtom);

const discoverTemplateFamily = atomFamily(
  (query: LibraryQuery | null) =>
    debugLabel(
      `discoverTemplateFamily/${JSON.stringify(query)}`,
      atom<Template | null>((get) => {
        if (query === null) return null;
        const template = get(libraryTemplateFamily(query));
        const branding = get(brandingAtom);

        // Discover does not support custom branding
        if (branding.branding_type.type === "custom") {
          console.error("Discover does not support custom branding", branding);
          return null;
        }

        const brandTone = get(brandToneAtom);
        const customTone = brandTone;

        if (!template) return null;

        const professional: LoadableTemplateContent = get(
          loadableDiscoverContentFamily({ template, contentKey: "professional" }),
        );
        const casual: LoadableTemplateContent = get(
          loadableDiscoverContentFamily({ template, contentKey: "casual" }),
        );
        const direct: LoadableTemplateContent = get(
          loadableDiscoverContentFamily({ template, contentKey: "direct" }),
        );
        const custom: LoadableTemplateContent | null = customTone
          ? get(loadableDiscoverContentFamily({ template, contentKey: "custom", customTone }))
          : null;

        return {
          ...template,
          content: {
            professional,
            casual,
            direct,
            ...(custom ? { custom } : null),
          },
          subject_line: template?.content.email_subject,
          images: template
            ? brandKitStyles.map((style) =>
                get(
                  loadableDiscoverTemplateImageFamily({
                    slug: template.slug,
                    img: template.img,
                    style: { type: "standard", value: style },
                  }),
                ),
              )
            : [],
          selectedImage: brandKitStyles.indexOf(branding.branding_type.value),
          channel: null,
        };
      }),
    ),
  isEqual,
);

export const discoverSearchQuery = atom<string | null>(null);

export const discoverSearchResultsAtom = debugLabel(
  `discoverSearchResults`,
  loadable(
    atom(async (get) => {
      const query = get(discoverSearchQuery);

      const token = await get(getTokenAtom).getToken();

      if (!query || query === "" || !token) return null;

      const unstable = get(unstableAtlasSearchAtom);

      const results = await atlasV2Search(query, token, unstable);

      const libraryTemplates = get(libraryAtom).programs.flatMap((p) => p.templates);

      if (!results) return libraryTemplates;

      return results
        .map((result) => {
          const template = libraryTemplates.find(
            (t) => t.program.slug === result.program && t.slug === result.slug,
          );

          if (!template) {
            console.error(`Template not found: ${result.program}/${result.slug}`);
            return null;
          }

          return template;
        })
        .filter(notEmpty);
    }),
  ),
);
